import { format, isValid, parseISO } from "date-fns";
import sbcLogo from "@/assets/sbc-logo-light-canvas-1200x400.png";
import {
  ACCEPTANCE_STATEMENT,
  CLIENT_GST_LABEL_BY_INTERNAL,
  CLIENT_STATUS_LABEL_BY_INTERNAL,
  DEFAULT_DELIVERY_CODE,
  ENGAGEMENT_EXPECTED_OUTCOMES,
  ENGAGEMENT_HOW_WE_WORK,
  ENGAGEMENT_PURPOSE_STATEMENT,
  ENGAGEMENT_TYPICAL_EXCLUSIONS,
  ENGAGEMENT_TYPICAL_INCLUSIONS,
  IMPORTANT_INFORMATION_STATEMENT,
  getDeliveryMethodByCode
} from "@/lib/sbcStandard";

/*
  QB-02C — Client-facing document preview.

  Renders the SBC-001 v1.0 mandated section order:

    1. Brand Header
    2. Document Status Banner
    3. Engagement Overview
    4. Scope Summary
    5. Delivery Method
    6. Commercial Details
    7. Fee Schedule
    8. Important Information
    9. Acceptance
    10. Contact Information
    11. Footer

  This component is presentation-only and MUST NOT expose:
    - consultant prompts
    - recommendation controls
    - internal commercial notes (internalCommercialNotes)
    - edit indicators
    - QA / status controls
    - backend identifiers (quote id, ownerApproved flag)
    - recommendation source metadata

  The component is also the print / PDF surface. Print CSS in
  index.css hides non-printable UI and applies A4 portrait sizing.
*/

const OPERATOR_LEGAL_LINE =
  "Spiral Beyond Compliance is operated by JOHN MARTIN KENNEALLY ABN 82 097 590 964.";

function formatDateDisplay(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    const d = parseISO(value);
    if (isValid(d)) return format(d, "dd/MM/yyyy");
  }
  return String(value);
}

function formatAudNumber(raw) {
  if (raw === null || raw === undefined) return "";
  const s = String(raw).trim();
  if (s === "") return "";
  if (!/^-?\d+(?:\.\d+)?$/.test(s)) return s;
  const [intPart, decPart] = s.split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
}

function BulletList({ items, emptyMessage }) {
  const cleaned = (items || []).map((s) => String(s).trim()).filter(Boolean);
  if (cleaned.length === 0) {
    return (
      <p className="text-sm italic text-muted-foreground">{emptyMessage}</p>
    );
  }
  return (
    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-foreground/90">
      {cleaned.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );
}

// Splits a stored recommendation string that may contain "•" bullets or
// newlines into a list of items.
function splitBullets(value) {
  if (!value) return [];
  const s = String(value);
  // If bullet markers exist, split on those.
  if (s.includes("•")) {
    return s.split(/\n?\s*•\s*/).map((x) => x.trim()).filter(Boolean);
  }
  return s.split("\n").map((x) => x.trim()).filter(Boolean);
}

function Section({ id, heading, subheading, testid, children, avoidBreak = false }) {
  return (
    <section
      id={id}
      data-testid={testid}
      className={`space-y-3 pt-6 first:pt-0 ${avoidBreak ? "print:break-inside-avoid" : ""}`}
    >
      <header>
        <h3 className="font-display text-lg text-foreground">{heading}</h3>
        {subheading && (
          <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
            {subheading}
          </p>
        )}
      </header>
      {children}
    </section>
  );
}

function KV({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm text-foreground" data-testid={testid}>{value}</dd>
    </div>
  );
}

export function ClientDocumentPreview({
  data,
  deliveryCode,
  internalStatus,
  contact,
  documentGeneratedAt
}) {
  if (!data) return null;

  const statusLabel =
    CLIENT_STATUS_LABEL_BY_INTERNAL[internalStatus] ||
    CLIENT_STATUS_LABEL_BY_INTERNAL.DRAFT;

  const gstLabel = data.gstSetting
    ? (CLIENT_GST_LABEL_BY_INTERNAL[data.gstSetting] || "GST Not Yet Confirmed")
    : "GST Not Yet Confirmed";

  const method = getDeliveryMethodByCode(deliveryCode || DEFAULT_DELIVERY_CODE)
    || getDeliveryMethodByCode(DEFAULT_DELIVERY_CODE);

  const feeText = data.proposedFinalPrice
    ? `$${formatAudNumber(data.proposedFinalPrice)} AUD`
    : (data.pricingRangeMin
        ? `$${formatAudNumber(data.pricingRangeMin)} – $${formatAudNumber(data.pricingRangeMax)} AUD`
        : null);

  const depositText = data.depositAmount
    ? `Deposit of $${formatAudNumber(data.depositAmount)} AUD required to commence.`
    : null;

  const deliverablesItems = splitBullets(data.deliverables);
  const inclusionsItems = splitBullets(data.inclusions);
  const exclusionsItems = splitBullets(data.exclusions);
  const evidenceItems = splitBullets(data.evidenceRequiredFromProvider);
  const referralsText = data.additionalProfessionalReferrals || "";

  const genDate = documentGeneratedAt
    ? format(documentGeneratedAt, "dd/MM/yyyy")
    : format(new Date(), "dd/MM/yyyy");

  return (
    <article
      data-testid="client-document-preview"
      className="print-document space-y-4 rounded-sm border-2 border-emerald-200/60 bg-white p-8 text-foreground print:border-0 print:p-0 print:shadow-none"
    >
      {/* 1. Brand Header — SBC-001 §2.5 */}
      <header
        data-testid="sbc-brand-header"
        className="flex items-start justify-between gap-6 border-b border-border pb-4 print:break-inside-avoid"
      >
        <div className="flex items-center gap-4">
          <img
            src={sbcLogo}
            alt="Spiral Beyond Compliance"
            className="h-14 w-auto object-contain"
            data-testid="preview-sbc-logo"
          />
          <div>
            <p className="font-display text-xl text-foreground">Spiral Beyond Compliance</p>
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
              Professional Consultancy Services
            </p>
          </div>
        </div>
        <div className="text-right text-sm">
          <p className="font-display text-lg text-foreground">Professional Services Quotation</p>
          <dl className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {data.quoteNumber && (
              <div><dt className="inline">Reference:</dt> <dd className="inline text-foreground">{data.quoteNumber}</dd></div>
            )}
            {data.version && (
              <div><dt className="inline">Version:</dt> <dd className="inline text-foreground">{data.version}</dd></div>
            )}
            {data.dateCreated && (
              <div><dt className="inline">Issued:</dt> <dd className="inline text-foreground">{formatDateDisplay(data.dateCreated)}</dd></div>
            )}
            {data.validUntil && (
              <div><dt className="inline">Valid until:</dt> <dd className="inline text-foreground">{formatDateDisplay(data.validUntil)}</dd></div>
            )}
          </dl>
        </div>
      </header>

      {/* 2. Document Status Banner — SBC-001 §1.8 / §2.6 */}
      <div
        data-testid="sbc-document-status-banner"
        className="rounded-sm border border-border bg-muted/60 px-4 py-2 text-xs uppercase tracking-[0.18em] text-foreground print:break-inside-avoid"
      >
        Document status: <span className="font-medium">{statusLabel}</span>
      </div>

      {/* 3. Engagement Overview — SBC-001 §2.7 */}
      <Section
        id="sbc-engagement-overview"
        testid="sbc-engagement-overview"
        heading="Engagement overview"
        subheading="Purpose of this engagement"
        avoidBreak
      >
        <p className="text-sm text-foreground/90">{ENGAGEMENT_PURPOSE_STATEMENT}</p>
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
            What this engagement includes
          </p>
          <BulletList
            items={ENGAGEMENT_TYPICAL_INCLUSIONS}
            emptyMessage="No inclusions have been defined for this engagement."
          />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
            What this engagement does not include
          </p>
          <BulletList
            items={ENGAGEMENT_TYPICAL_EXCLUSIONS}
            emptyMessage="No exclusions have been defined for this engagement."
          />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">How we work</p>
          <p className="mt-1 text-sm text-foreground/90">{ENGAGEMENT_HOW_WE_WORK}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Expected outcomes</p>
          <p className="mt-1 text-sm text-foreground/90">{ENGAGEMENT_EXPECTED_OUTCOMES}</p>
        </div>
      </Section>

      {/* 4. Scope Summary — SBC-001 §2.8 (NO price in this section) */}
      <Section
        id="sbc-scope-summary"
        testid="sbc-scope-summary"
        heading="Scope summary"
        avoidBreak
      >
        <dl className="space-y-2">
          {data.serviceLabel && <KV label="Selected service" value={data.serviceLabel} testid="scope-service" />}
          <KV label="Delivery method" value={method?.label} testid="scope-delivery-method" />
          <KV label="Primary objective" value={data.assessmentScope || null} testid="scope-primary-objective" />
          <KV
            label="Major deliverables"
            value={deliverablesItems.length ? deliverablesItems.join("; ") : null}
            testid="scope-major-deliverables"
          />
          <KV label="Estimated duration" value={data.estimatedTimeframe || null} testid="scope-duration" />
          <KV
            label="Evidence expectations"
            value={evidenceItems.length ? evidenceItems.join("; ") : null}
            testid="scope-evidence"
          />
        </dl>
        {!data.assessmentScope && !deliverablesItems.length && !evidenceItems.length && (
          <p className="text-sm italic text-muted-foreground">
            Scope details have not been added to this quotation yet.
          </p>
        )}
      </Section>

      {/* 5. Delivery Method — SBC-001 §2.9 / §1.10 */}
      <Section
        id="sbc-delivery-method"
        testid="sbc-delivery-method"
        heading="Delivery method"
        subheading={method?.code || ""}
        avoidBreak
      >
        <p className="text-sm text-foreground/90">
          <span className="font-medium">{method?.label}</span> — {method?.description}
        </p>
      </Section>

      {/* 6. Commercial Details — SBC-001 §2.10 */}
      <Section
        id="sbc-commercial-details"
        testid="sbc-commercial-details"
        heading="Commercial details"
        avoidBreak
      >
        {data.assessmentScope && (
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Assessment scope</p>
            <p className="mt-1 whitespace-pre-line text-sm text-foreground/90">{data.assessmentScope}</p>
          </div>
        )}
        {data.documentReviewScope && (
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Documentation review</p>
            <p className="mt-1 whitespace-pre-line text-sm text-foreground/90">{data.documentReviewScope}</p>
          </div>
        )}
        {deliverablesItems.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Deliverables</p>
            <BulletList items={deliverablesItems} emptyMessage="No deliverables have been defined for this quotation." />
          </div>
        )}
        {inclusionsItems.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Inclusions</p>
            <BulletList items={inclusionsItems} emptyMessage="No inclusions have been defined for this quotation." />
          </div>
        )}
        {exclusionsItems.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Exclusions</p>
            <BulletList items={exclusionsItems} emptyMessage="No exclusions have been defined for this quotation." />
          </div>
        )}
        {evidenceItems.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Client responsibilities — evidence</p>
            <BulletList items={evidenceItems} emptyMessage="No evidence expectations have been defined for this quotation." />
          </div>
        )}
        {referralsText && (
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">External professional referrals</p>
            <p className="mt-1 whitespace-pre-line text-sm text-foreground/90">{referralsText}</p>
          </div>
        )}
        {!data.assessmentScope && !data.documentReviewScope && deliverablesItems.length === 0 && inclusionsItems.length === 0 && exclusionsItems.length === 0 && (
          <p className="text-sm italic text-muted-foreground">
            No commercial detail has been added to this quotation yet.
          </p>
        )}
      </Section>

      {/* 7. Fee Schedule — SBC-001 §2.12 */}
      <Section
        id="sbc-fee-schedule"
        testid="sbc-fee-schedule"
        heading="Fee schedule"
        avoidBreak
      >
        <dl className="space-y-2">
          <KV
            label="Pricing basis"
            value={data.pricingBasis === "HOURLY" ? "Hourly rate" : "Fixed price range"}
            testid="fee-basis"
          />
          <KV label="Professional fee" value={feeText || "To be confirmed before invoicing"} testid="fee-professional" />
          <KV label="GST position" value={gstLabel} testid="fee-gst" />
          <KV label="Travel" value="Not required unless expressly stated in the scope above." testid="fee-travel" />
          <KV
            label="Additional services"
            value="Any additional services requested outside the agreed scope will be quoted separately in writing before commencement."
            testid="fee-additional"
          />
          <KV
            label="Validity period"
            value={
              data.validUntil
                ? `This quotation is valid until ${formatDateDisplay(data.validUntil)}. If not accepted by that date, please request a fresh quotation.`
                : "If a validity date is not shown in the header, please request a fresh quotation before acceptance."
            }
            testid="fee-validity"
          />
          <KV
            label="Payment terms"
            value={
              depositText
                ? `${depositText} ${data.balanceTerms || "Balance due on delivery of the agreed deliverables, per manually issued invoice."}`
                : (data.balanceTerms || "Payment due per manually issued invoice.")
            }
            testid="fee-payment-terms"
          />
        </dl>
        <p className="mt-3 text-xs italic text-muted-foreground">
          The proposed professional fee has been calculated using the agreed consultancy scope described within this document.
          Should the agreed scope materially change, Spiral Beyond Compliance may recommend an amended quotation before undertaking additional work.
        </p>
      </Section>

      {/* 8. Important Information — SBC-001 §2.16 / §12 */}
      <Section
        id="sbc-important-information"
        testid="sbc-important-information"
        heading="Important information"
        avoidBreak
      >
        {IMPORTANT_INFORMATION_STATEMENT.map((line, idx) => (
          <p key={idx} className="text-sm text-foreground/90">{line}</p>
        ))}
      </Section>

      {/* 9. Acceptance — SBC-001 §2.17 / §13 */}
      <Section
        id="sbc-acceptance"
        testid="sbc-acceptance"
        heading="Acceptance"
        avoidBreak
      >
        {ACCEPTANCE_STATEMENT.map((line, idx) => (
          <p key={idx} className="text-sm text-foreground/90">{line}</p>
        ))}
      </Section>

      {/* 10. Contact Information — SBC-001 §2.18 */}
      <Section
        id="sbc-contact"
        testid="sbc-contact"
        heading="Contact information"
        avoidBreak
      >
        <dl className="space-y-1 text-sm">
          <KV label="Consultant" value={contact?.consultantName || "John Kenneally"} />
          <KV label="Business" value="Spiral Beyond Compliance" />
          <KV label="Email" value={contact?.email || "john@spiralbeyondcompliance.com.au"} />
          {contact?.phone && <KV label="Telephone" value={contact.phone} />}
          <KV label="Website" value={contact?.website || "spiralbeyondcompliance.com.au"} />
          {contact?.hours && <KV label="Business hours" value={contact.hours} />}
        </dl>
      </Section>

      {/* 11. Footer — SBC-001 §2.19 */}
      <footer
        data-testid="sbc-footer"
        className="mt-6 border-t border-border pt-3 text-[11px] leading-relaxed text-muted-foreground print:break-inside-avoid"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>Spiral Beyond Compliance</span>
          {data.quoteNumber && <span>Reference: {data.quoteNumber}</span>}
          {data.version && <span>Version: {data.version}</span>}
          <span>Confidential</span>
          <span>Generated: {genDate}</span>
        </div>
        <p className="mt-1">{OPERATOR_LEGAL_LINE}</p>
      </footer>
    </article>
  );
}
